import React, { Component } from 'react';
import { HorizontalBar } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import assignColors from 'utils/assignColors';
import { connect } from 'react-redux';
import styled from 'styled-components';
import RingChart from '../../../../components/RingChart/RingChart';
import AssetLegend from '../../../../components/AssetLegend';

@connect((state) => ({
  exchangeRate: state.Storage['pwpa-exchange-rate'],
}))
class AssetChart extends Component {
  getChartData = (accounts) => {
    let hasCADAccounts = false;
    const exchangeRate =
      Number(localStorage.getItem('pwpa-exchange-rate')) || 1;

    const filteredAccounts = accounts.filter((account) => {
      if (account.currency === 'CAD') {
        hasCADAccounts = true;
      }
      return Object.values(account.asset_allocation).some(
        (value) => value !== 0
      );
    });

    const getUnmatchedValue = (account) =>
      (account.holdings || [])
        .filter((holding) => !holding.security_detail)
        .reduce((sum, holding) => {
          let value = holding.market_value;
          if (account.currency === 'USD' && hasCADAccounts) {
            value *= exchangeRate;
          }
          return sum + value;
        }, 0);
    const totalHoldings = filteredAccounts.reduce((total, account) => {
      const unmatched = getUnmatchedValue(account, hasCADAccounts);

      const balance =
        account.currency === 'USD' && hasCADAccounts
          ? (account.balance - account.cash) * exchangeRate - unmatched
          : account.balance - account.cash - unmatched;

      return total + balance;
    }, 0);

    const categories = ['Equity', 'Fixed Income', 'Alternative', 'Misc'];
    const allHoldings = filteredAccounts.reduce((result, account) => {
      const { asset_allocation } = account;
      const unmatched = getUnmatchedValue(account);

      const accountTotalHoldings =
        account.currency === 'USD' && hasCADAccounts
          ? (account.balance - account.cash) * exchangeRate - unmatched
          : account.balance - account.cash - unmatched;

      const weight = accountTotalHoldings / Math.abs(totalHoldings || 1);

      categories.forEach((category) => {
        const label = category === 'Misc' ? 'Misc.' : category;
        result[label] += asset_allocation[category] * weight;
      });

      return result;
    }, Object.fromEntries(categories.map((category) => [category === 'Misc' ? 'Misc.' : category, 0])));

    let hasNegative = false;
    const formattedHoldings = {};
    let totalAssetAllocation = 0;

    Object.entries(allHoldings).forEach(([category, value]) => {
      if (value < 0) {
        hasNegative = true;
      }
      if (value !== 0) {
        formattedHoldings[category] = value;
        totalAssetAllocation += value;
      }
    });

    return {
      labels: Object.keys(formattedHoldings),
      values: Object.values(formattedHoldings),
      filtered: formattedHoldings,
      negative: hasNegative,
      datasets: [
        {
          data: Object.values(formattedHoldings),
          backgroundColor: assignColors(Object.keys(formattedHoldings)),
        },
      ],
      totalAssetAllocation,
    };
  };

  render() {
    const { accounts } = this.props;
    const chartData = this.getChartData(accounts);

    // Chart options for barchart that shows when there is a negative value
    const chartOptions = {
      showAllTooltips: true,
      maintainAspectRatio: false,
      cutoutPercentage: 50,
      responsive: true,
      layout: {
        padding: {
          left: 0,
          right: 0,
          bottom: 0,
          top: 0,
        },
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title: (tooltipItems, data) => data.labels[tooltipItems[0].index],
          label: (tooltipItems, data) =>
            `${
              Math.round(
                data.datasets[tooltipItems.datasetIndex].data[
                  tooltipItems.index
                ] * 10000
              ) / 100
            }%`,
        },
      },
      hover: {
        onHover: (e) => {
          e.target.style.cursor = 'pointer';
        },
      },
    };

    return (
      <div>
        {chartData.negative ? (
          <BarChartWrap>
            <HorizontalBar
              data={chartData}
              options={chartOptions}
              style={{
                margin: '0 4rem 0 0',
              }}
            />
          </BarChartWrap>
        ) : (
          <RingChartWrap>
            <AssetLegend data={chartData.filtered} />
            <RingChart
              labels={chartData.labels}
              values={chartData.values}
              colors={assignColors(chartData.labels)}
              maxHeight={350}
              maxWidth={350}
            />
          </RingChartWrap>
        )}
      </div>
    );
  }
}

AssetChart.propTypes = {
  accounts: PropTypes.array,
  exchangeRate: PropTypes.number,
};

AssetChart.defaultProps = {
  accounts: [],
  exchangeRate: 0,
};

export default AssetChart;

const RingChartWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  column-gap: 1rem;
`;

const BarChartWrap = styled.div`
  height: 350px;
`;
